import React, { useCallback, useEffect, useState } from "react";
import { Switch, Button } from "antd";
import { Line } from "react-chartjs-2";
import { COLORS } from "../../consts";
import { zoomRect } from "chartjs-plugin-zoom";

const LineChart = ({
  classes,
  lineData,
  currentMetric,
  siteMetadata,
  isCumulative,
  setIsCumulative,
  maxWidth = 1120,
  isSubplot = false,
}) => {
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });

  const toggleZoom = useCallback(() => {
    setZoomEnabled(!zoomEnabled);
  }, [zoomEnabled]);

  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };

  const getMetricLabel = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.label;
    }
    return "Unknown Metric";
  };

  const getMetricValue = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.value;
    }
    return "Unknown Metric";
  };

  useEffect(() => {
    if (lineData) {
      setData({
        labels:
          lineData.labels?.map((label) =>
            new Date(label).toString().split(" ").slice(1, 4),
          ) || [],
        datasets: lineData.data?.map((line, idx) => ({
          label: line.name || "",
          data: line.val || [],
          backgroundColor: COLORS.graphColors[idx],
          borderColor: COLORS.graphColors[idx],
          borderWidth: 2,
          borderJoinStyle: "round",
          lineTension: 0.5,
        })),
      });
    } else {
      setData({
        labels: [],
        datasets: [],
      });
    }
  }, [lineData]);

  const options = {
    responsive: true,
    // maintainAspectRatio: false,
    elements: {
      line: {
        borderJoinStyle: "round",
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: `${getMetricLabel(currentMetric)} ${
            siteMetadata?.units &&
            siteMetadata?.units[getMetricValue(currentMetric)]
              ? `(${siteMetadata?.units[getMetricValue(currentMetric)]})`
              : ""
          }`,
          font: {
            family: "'museoSansBold', sans-serif",
            size: "16px",
          },
        },
      },
      x: {
        title: {
          display: true,
          text: "Date",
          font: {
            family: "'museoSansBold', sans-serif",
            size: "16px",
          },
        },
      },
    },
    plugins: {
      zoom: {
        pan: {
          enabled: zoomEnabled,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: zoomEnabled,
          },
          pinch: {
            enabled: zoomEnabled,
          },
          mode: "xy",
        },
      },
    },
  };

  return (
    <div
      className={`w-full flex flex-col items-center mb-10 p-4 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101 ${
        classes ? classes : ""
      }`}
      id="linechart"
    >
      <div id="linecontent" className={`w-full max-w-[${maxWidth}px]`}>
        <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
          {`${isSubplot ? "Subplot graph :" : ""} ${getMetricLabel(currentMetric)}`}
        </p>
        <div id="linecontrols" className="flex justify-end mb-5">
          <div className="mr-2 zoom">
            <Button
              className="rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
              type={zoomEnabled ? "primary" : "dashed"}
              onClick={toggleZoom}
            >
              {`${zoomEnabled ? "Disable" : "Enable"} Zoom`}
            </Button>
          </div>
          {siteMetadata?.cumulative_metrics?.includes(
            getMetricValue(currentMetric),
          ) ? (
            <div className="flex items-center cumulative">
              <p className="text-sm  pr-2 ">Enable Cumulative</p>
              <Switch className='hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110' checked={isCumulative} onChange={setIsCumulative} />
            </div>
          ) : null}
        </div>
        <div className={`w-full flex justify-center`}>
          <Line data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default LineChart;
